import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, MessageSquarePlus, Paperclip, Send, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, PageHeader } from "@/components/shared/Common";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { NewThreadDialog } from "./NewThreadDialog";

const openFile = async (f) => {
  try { const r = await api.get(`/files/${f.id}`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch (e) { toast.error(errMsg(e)); }
};

const ThreadList = ({ threads, active, onPick }) => (
  <div className="divide-y divide-[#f0f2ee]" data-testid="thread-list">
    {threads.length === 0 && <EmptyState title="Sin conversaciones" text="Inicie una con el botón Nueva conversación." />}
    {threads.map((t) => (
      <button key={t.id} data-testid={`thread-item-${t.id}`} onClick={() => onPick(t.id)}
        className={`w-full text-left px-4 py-3 hover:bg-[#f6fbf2] transition-colors ${active === t.id ? "bg-[#eef8e6]" : ""}`}>
        <div className="flex items-center gap-2">
          {t.type === "group" && <Users size={14} className="text-[#258F00] shrink-0" />}
          <span className="font-semibold text-sm truncate flex-1">{t.title}</span>
          {t.unread > 0 && <span data-testid={`thread-unread-${t.id}`} className="rounded-full bg-[#5CD500] px-1.5 text-[10px] font-bold text-[#0f1a08]">{t.unread}</span>}
        </div>
        <div className="text-xs text-[#5b6656] truncate">{t.last_preview || "Sin mensajes"}</div>
      </button>))}
  </div>
);

const Bubble = ({ m }) => (
  <div className={`flex ${m.mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.mine ? "bg-[#258F00] text-white rounded-br-sm" : "bg-[#f0f2ee] text-[#1a2416] rounded-bl-sm"}`}>
      {!m.mine && <div className="text-[11px] font-bold text-[#258F00] mb-0.5">{m.sender_name}</div>}
      {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
      {m.files.map((f) => <button key={f.id} data-testid={`message-file-${f.id}`} onClick={() => openFile(f)} className="mt-1 flex items-center gap-1 underline text-xs"><Paperclip size={12} />{f.name}</button>)}
      <div className={`text-[10px] mt-1 ${m.mine ? "text-white/70" : "text-[#8a9584]"}`}>{fmtDateTime(m.created_at)}</div>
    </div>
  </div>
);

const Composer = ({ threadId, onSent }) => {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("resource_type", "chat_thread"); fd.append("resource_id", threadId);
    setBusy(true);
    try { const { data } = await api.post("/files", fd); setFiles([...files, data]); } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const send = async () => {
    if (!text.trim() && !files.length) return;
    setBusy(true);
    try { await api.post(`/chat/threads/${threadId}/messages`, { body: text, file_ids: files.map((f) => f.id) }); setText(""); setFiles([]); onSent(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-[#e4e8e1] p-3">
      {files.length > 0 && <div className="mb-2 flex flex-wrap gap-2 text-xs">{files.map((f) => <span key={f.id} className="rounded-full bg-[#f0f2ee] px-2 py-0.5">{f.original_name}</span>)}</div>}
      <div className="flex items-end gap-2">
        <input ref={input} type="file" className="hidden" onChange={upload} accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" data-testid="chat-file-input" />
        <Button data-testid="chat-attach-btn" size="icon" variant="outline" className="rounded-full shrink-0" disabled={busy} onClick={() => input.current?.click()}><Paperclip size={16} /></Button>
        <Textarea data-testid="chat-input" rows={1} className="min-h-[40px] resize-none" placeholder="Escriba un mensaje…" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <Button data-testid="chat-send-btn" size="icon" className="rounded-full shrink-0" disabled={busy} onClick={send}><Send size={16} /></Button>
      </div>
    </div>
  );
};

const Conversation = ({ thread, onBack, onChange }) => {
  const [msgs, setMsgs] = useState([]);
  const end = useRef(null);
  const load = () => api.get(`/chat/threads/${thread.id}/messages`).then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [thread.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs.length]);
  return (
    <div className="flex h-full flex-col" data-testid="conversation">
      <div className="flex items-center gap-2 border-b border-[#e4e8e1] px-4 py-3">
        <button className="lg:hidden" onClick={onBack} data-testid="conversation-back-btn"><ArrowLeft size={18} /></button>
        <div className="min-w-0">
          <div className="font-semibold truncate" data-testid="conversation-title">{thread.title}</div>
          {thread.type === "group" && <div className="text-xs text-[#5b6656] truncate">{thread.members.map((m) => m.name).join(", ")}</div>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto space-y-2 p-4">{msgs.map((m) => <Bubble key={m.id} m={m} />)}<div ref={end} /></div>
      <Composer threadId={thread.id} onSent={() => { load(); onChange(); }} />
    </div>
  );
};

export default function Messages() {
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [open, setOpen] = useState(false);
  const active = params.get("t");
  const load = () => api.get("/chat/threads").then((r) => setThreads(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);
  const pick = (id) => { setParams(id ? { t: id } : {}); };
  const thread = threads.find((t) => t.id === active);
  return (
    <div data-testid="messages-page">
      <PageHeader eyebrow="Comunicación" title="Mensajes" subtitle="Mensajería interna entre usuarios de SYC CONTROL."
        actions={<Button data-testid="new-thread-btn" className="rounded-full" onClick={() => setOpen(true)}><MessageSquarePlus size={15} className="mr-1" />Nueva conversación</Button>} />
      <div className="syc-card overflow-hidden grid lg:grid-cols-[320px_1fr] h-[calc(100vh-240px)] min-h-[420px]">
        <div className={`border-r border-[#e4e8e1] overflow-y-auto ${thread ? "hidden lg:block" : ""}`}><ThreadList threads={threads} active={active} onPick={pick} /></div>
        <div className={`${thread ? "" : "hidden lg:flex"} min-h-0`}>
          {thread ? <Conversation thread={thread} onBack={() => pick(null)} onChange={load} /> : <div className="m-auto"><EmptyState title="Seleccione una conversación" /></div>}
        </div>
      </div>
      <NewThreadDialog open={open} onClose={() => setOpen(false)} onCreated={(t) => { setOpen(false); load().then(() => pick(t.id)); }} />
    </div>
  );
}
